import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Radio,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useQueryClient } from '@tanstack/react-query';
import { uploadWp } from '../../api/dev';
import { useSnackbar } from '../../context/SnackbarContext';
import type { WorkPackage, Resolution } from '../../types/dev';

// ── Front-matter parser (client-side) ────────────────────────────────────

interface FrontMatterPreview {
  wp: string | null;
  title: string | null;
  generated: string | null;
  subtasks: string[];
  phase: number | null;
  priority: number | null;
  lineCount: number;
  error: string | null;
}

function extractH1(text: string): string | null {
  const m = /^#\s+(.+)/m.exec(text);
  return m ? m[1].trim() : null;
}

function parseClientFrontMatter(text: string): FrontMatterPreview {
  const normalized = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n');
  const lineCount = lines.length;
  const blank: FrontMatterPreview = { wp: null, title: null, generated: null, subtasks: [], phase: null, priority: null, lineCount, error: null };

  if (lines[0]?.trim() !== '---') {
    const title = extractH1(normalized);
    return { ...blank, title, error: title ? null : 'No front-matter. Title is required.' };
  }
  const closingIdx = lines.indexOf('---', 1);
  if (closingIdx === -1) {
    const title = extractH1(normalized);
    return { ...blank, title, error: title ? null : 'Front-matter not closed.' };
  }

  const fmLines = lines.slice(1, closingIdx);
  let wp: string | null = null, title: string | null = null, generated: string | null = null;
  let phase: number | null = null, priority: number | null = null;
  const subtasks: string[] = [];
  let inSubtasks = false;

  for (const line of fmLines) {
    if (/^wp:\s*(.+)/.test(line))        { wp = line.replace(/^wp:\s*/, '').trim(); inSubtasks = false; }
    else if (/^title:\s*(.+)/.test(line)) { title = line.replace(/^title:\s*/, '').trim(); inSubtasks = false; }
    else if (/^generated:\s*(.+)/.test(line)) { generated = line.replace(/^generated:\s*/, '').trim(); inSubtasks = false; }
    else if (/^phase:\s*(\d+)/.test(line)) { phase = parseInt(line.replace(/^phase:\s*/, ''), 10); inSubtasks = false; }
    else if (/^priority:\s*(\d+)/.test(line)) { priority = parseInt(line.replace(/^priority:\s*/, ''), 10); inSubtasks = false; }
    else if (/^subtasks:/.test(line)) { inSubtasks = true; }
    else if (inSubtasks && /^\s+-\s+(.+)/.test(line)) {
      const m = /^\s+-\s+(.+)/.exec(line); if (m) subtasks.push(m[1]);
    } else { inSubtasks = false; }
  }

  if (!title) return { ...blank, wp, generated, subtasks, phase, priority, lineCount, error: 'Missing required "title" field.' };
  return { wp, title, generated, subtasks, phase, priority, lineCount, error: null };
}

// ── Collision detection ───────────────────────────────────────────────────

function nextWpString(cached: WorkPackage[]): string {
  const nums = cached.map((w) => parseInt(w.wp_number.slice(2), 10)).filter((n) => !isNaN(n));
  return `WP${(nums.length > 0 ? Math.max(...nums) : 0) + 1}`;
}

type Collision =
  | { kind: 'free' }
  | { kind: 'overwrite'; existing: WorkPackage }
  | { kind: 'taken'; existing: WorkPackage; suggestedNext: string };

type Resolution2 = 'next' | 'overwrite' | 'idea';

// ── ResolutionCard ────────────────────────────────────────────────────────

function ResolutionCard({
  label, sublabel, selected, disabled, onClick,
}: {
  label: string; sublabel?: string;
  selected: boolean; disabled?: boolean; onClick: () => void;
}) {
  const theme = useTheme();
  return (
    <Box
      onClick={!disabled ? onClick : undefined}
      sx={{
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 1,
        p: 1.2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.38 : 1,
        bgcolor: selected ? alpha(theme.palette.primary.main, 0.07) : 'transparent',
        transition: 'border-color 0.15s, background-color 0.15s',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Radio size="small" checked={selected} disabled={disabled} sx={{ p: 0 }} onChange={onClick} />
        <Box>
          <Typography variant="body2" fontWeight={selected ? 600 : 400}>{label}</Typography>
          {sublabel && <Typography variant="caption" color="text.secondary">{sublabel}</Typography>}
        </Box>
      </Stack>
    </Box>
  );
}

// ── SubtaskRow ────────────────────────────────────────────────────────────

const MONO = '"ui-monospace","SF Mono",Menlo,monospace';

function SubtaskRow({
  idx, text, badge, solved, onToggle,
}: {
  idx: number; text: string; badge: 'KEEP' | 'NEW';
  solved: boolean; onToggle: () => void;
}) {
  const theme = useTheme();
  const rowBg = solved
    ? alpha(theme.palette.success.main, 0.12)
    : badge === 'NEW' ? alpha(theme.palette.success.main, 0.06) : 'transparent';

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '24px 28px 1fr auto',
        gap: 0.5,
        alignItems: 'center',
        py: 0.4,
        px: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: rowBg,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Checkbox
        size="small"
        checked={solved}
        onChange={onToggle}
        sx={{
          p: 0,
          color: solved ? 'success.main' : 'action.disabled',
          '&.Mui-checked': { color: 'success.main' },
        }}
      />
      <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', color: 'text.disabled', userSelect: 'none' }}>
        {String(idx).padStart(2, '0')}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: solved ? 'text.secondary' : 'text.primary',
          textDecoration: solved ? 'line-through' : 'none',
          fontSize: '0.8rem',
          lineHeight: 1.4,
        }}
      >
        {text}
      </Typography>
      <Chip
        label={badge}
        size="small"
        sx={{
          fontFamily: MONO,
          fontSize: '0.62rem',
          height: 18,
          bgcolor: badge === 'NEW' ? alpha(theme.palette.success.main, 0.15) : 'transparent',
          color: badge === 'NEW' ? 'success.main' : 'text.disabled',
          border: '1px solid',
          borderColor: badge === 'NEW' ? 'success.main' : 'divider',
        }}
      />
    </Box>
  );
}

// ── Main dialog ───────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

export function UploadWpDialog({ open, onClose }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<FrontMatterPreview | null>(null);
  const [collision, setCollision] = useState<Collision>({ kind: 'free' });
  const [resolution, setResolution] = useState<Resolution2>('next');
  const [priorityStr, setPriorityStr] = useState('');
  const [forcePriority, setForcePriority] = useState(false);
  const [solved, setSolved] = useState<boolean[]>([]);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  useEffect(() => {
    setSolved(preview ? preview.subtasks.map(() => false) : []);
  }, [preview?.subtasks.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setApiError(null);
    setForcePriority(false);
    if (!f) { setPreview(null); setPriorityStr(''); setCollision({ kind: 'free' }); return; }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const fm = parseClientFrontMatter(text);
      setPreview(fm);
      setPriorityStr(fm.priority != null ? String(fm.priority) : '');

      const cached = queryClient.getQueryData<WorkPackage[]>(['dev', 'wps']) ?? [];
      const existing = fm.wp ? cached.find((w) => w.wp_number === fm.wp) : null;
      const titleMatches = existing && existing.title.trim().toLowerCase() === fm.title?.trim().toLowerCase();

      const col: Collision = !existing
        ? { kind: 'free' }
        : titleMatches
        ? { kind: 'overwrite', existing }
        : { kind: 'taken', existing, suggestedNext: nextWpString(cached) };
      setCollision(col);
      setResolution(!existing ? 'next' : titleMatches ? 'overwrite' : 'next');
    };
    reader.readAsText(f);
  };

  const handleUpload = async () => {
    if (!file || !preview || preview.error) return;
    setLoading(true);
    setApiError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('resolution', resolution);
      form.append('priority', priorityStr || '100');
      form.append('force_priority', forcePriority ? '1' : '0');
      const solvedIdx = solved.map((s, i) => s ? i : -1).filter((i) => i >= 0);
      form.append('solved_subtasks', JSON.stringify(solvedIdx));

      const { wp, resolution: res } = await uploadWp(form);
      await queryClient.invalidateQueries({ queryKey: ['dev', 'wps'] });

      const msg = snackbarCopy(res, wp.wp_number);
      showSnackbar(msg, 'success');
      handleClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Upload failed';
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null); setPreview(null); setApiError(null);
    setCollision({ kind: 'free' }); setResolution('next');
    setPriorityStr(''); setForcePriority(false); setSolved([]);
    if (inputRef.current) inputRef.current.value = '';
    onClose();
  };

  const priorityValid = priorityStr === '' || (/^\d+$/.test(priorityStr) && parseInt(priorityStr, 10) >= 1 && parseInt(priorityStr, 10) <= 65535);

  // Subtask diff badges
  const existingSubs = collision.kind === 'overwrite' ? (collision.existing.subtasks ?? []) : [];
  const badges = preview?.subtasks.map((t) =>
    existingSubs.some((s) => s.text === t) ? 'KEEP' : 'NEW',
  ) as ('KEEP' | 'NEW')[] | undefined;

  const solvedCount = solved.filter(Boolean).length;
  const allSolved = solved.length > 0 && solvedCount === solved.length;

  // Action label
  const actionLabel = (() => {
    if (resolution === 'idea') return 'Save as Idea';
    if (resolution === 'overwrite' && collision.kind === 'overwrite') return `Overwrite ${collision.existing.wp_number}`;
    if (collision.kind === 'taken') return `Create ${collision.suggestedNext}`;
    return `Create ${preview?.wp ?? '…'}`;
  })();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Upload Feature Markdown
        {preview && !preview.error && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
            {file?.name} · {preview.lineCount} lines · {preview.subtasks.length} subtasks
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <input ref={inputRef} type="file" accept=".md" style={{ display: 'none' }} onChange={handleFileChange} />

        {!file ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => inputRef.current?.click()}>
              Select .md file
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: 3 }}>
            {/* ── Left sidebar ── */}
            <Stack spacing={2}>
              {/* FilePill */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  onClick={() => inputRef.current?.click()}
                  sx={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', justifyContent: 'flex-start' }}
                >
                  {file.name}
                </Button>
              </Stack>

              {preview?.error && <Alert severity="error" sx={{ py: 0 }}>{preview.error}</Alert>}

              {/* Front-matter KV list */}
              {preview && !preview.error && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                  <Stack spacing={0.5}>
                    {[
                      ['WP', preview.wp ?? 'AUTO'],
                      ['Title', preview.title ?? '—'],
                      ...(preview.generated ? [['Generated', preview.generated]] : []),
                      ...(preview.phase != null ? [['Phase', String(preview.phase)]] : []),
                      ['Subtasks', String(preview.subtasks.length)],
                    ].map(([k, v]) => (
                      <Stack key={k} direction="row" spacing={1} alignItems="baseline">
                        <Typography variant="caption" color="text.secondary" sx={{ width: 64, flexShrink: 0 }}>{k}</Typography>
                        <Typography variant="caption" fontWeight={k === 'Title' ? 600 : 400} sx={{ wordBreak: 'break-all' }}>{v}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                </Box>
              )}

              {preview && !preview.error && (
                <>
                  {/* Tracking segmented */}
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">Track as</Typography>
                    <Stack direction="row" spacing={1}>
                      {(['next', 'overwrite'] as const).some((r) => r === resolution) ? (
                        <>
                          <Button size="small" variant="contained" disableElevation sx={{ flex: 1 }}>Work Package</Button>
                          <Button size="small" variant="outlined" sx={{ flex: 1 }} onClick={() => setResolution('idea')}>Idea</Button>
                        </>
                      ) : (
                        <>
                          <Button size="small" variant="outlined" sx={{ flex: 1 }} onClick={() => setResolution(collision.kind === 'overwrite' ? 'overwrite' : 'next')}>Work Package</Button>
                          <Button size="small" variant="contained" disableElevation sx={{ flex: 1 }}>Idea</Button>
                        </>
                      )}
                    </Stack>
                  </Stack>

                  {/* Priority field */}
                  <TextField
                    label="Priority"
                    size="small"
                    value={priorityStr}
                    onChange={(e) => setPriorityStr(e.target.value)}
                    placeholder="100"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    error={!priorityValid}
                    helperText={!priorityValid ? '1–65535' : undefined}
                  />

                  {/* Force-priority checkbox */}
                  <Tooltip title={resolution !== 'overwrite' ? 'Only applicable when overwriting an existing WP' : ''} placement="right">
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={forcePriority}
                          disabled={resolution !== 'overwrite'}
                          onChange={(e) => setForcePriority(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2" color={resolution === 'overwrite' ? 'text.primary' : 'text.disabled'}>
                          Force priority
                        </Typography>
                      }
                    />
                  </Tooltip>
                </>
              )}
            </Stack>

            {/* ── Right main pane ── */}
            <Stack spacing={1.5}>
              {preview?.error && <Alert severity="error">{preview.error}</Alert>}

              {preview && !preview.error && (
                <>
                  {/* Collision header */}
                  {collision.kind === 'taken' && (
                    <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ py: 0.5 }}>
                      <strong>{collision.existing.wp_number}</strong> is already in use by &ldquo;{collision.existing.title}&rdquo;.
                      Overwrite is disabled — the file will be uploaded as {collision.suggestedNext}.
                    </Alert>
                  )}
                  {collision.kind === 'overwrite' && (
                    <Alert severity="info" sx={{ py: 0.5 }}>
                      <strong>{collision.existing.wp_number}</strong> exists with the same title. Status and progress will be preserved on overwrite.
                    </Alert>
                  )}

                  {/* Resolution cards */}
                  <Stack spacing={0.75}>
                    <ResolutionCard
                      label={collision.kind === 'taken' ? `Create as ${collision.suggestedNext}` : `Create${preview.wp ? ` as ${preview.wp}` : ' with next free number'}`}
                      sublabel={collision.kind === 'taken' ? `${preview.wp} is taken — using next free` : undefined}
                      selected={resolution === 'next'}
                      onClick={() => setResolution('next')}
                    />
                    <ResolutionCard
                      label={collision.kind === 'overwrite' ? `Overwrite ${collision.existing.wp_number}` : 'Overwrite existing WP'}
                      sublabel={collision.kind !== 'overwrite' ? 'Not available — no matching WP found' : 'Preserves status, claim, and bug count'}
                      selected={resolution === 'overwrite'}
                      disabled={collision.kind !== 'overwrite'}
                      onClick={() => setResolution('overwrite')}
                    />
                    <ResolutionCard
                      label="Park as Idea"
                      sublabel="Saved but excluded from the WP queue"
                      selected={resolution === 'idea'}
                      onClick={() => setResolution('idea')}
                    />
                  </Stack>

                  {apiError && <Alert severity="error" sx={{ py: 0 }}>{apiError}</Alert>}

                  {preview.subtasks.length > 0 && (
                    <>
                      <Divider />

                      {/* Select-all row */}
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Checkbox
                          size="small"
                          checked={allSolved}
                          indeterminate={solvedCount > 0 && !allSolved}
                          onChange={() => setSolved(solved.map(() => !allSolved))}
                          sx={{ p: 0, color: 'action.disabled', '&.Mui-checked': { color: 'success.main' }, '&.MuiCheckbox-indeterminate': { color: 'success.main' } }}
                        />
                        <Typography variant="caption" color="text.secondary">Mark as already solved</Typography>
                        <Chip
                          label={`${solvedCount} / ${solved.length} solved`}
                          size="small"
                          color={solvedCount > 0 ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ ml: 'auto', height: 20, fontSize: '0.68rem' }}
                        />
                      </Stack>

                      {/* Subtask rows */}
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          maxHeight: 280,
                          overflowY: 'auto',
                        }}
                      >
                        {preview.subtasks.map((text, i) => (
                          <SubtaskRow
                            key={i}
                            idx={i}
                            text={text}
                            badge={badges?.[i] ?? 'NEW'}
                            solved={solved[i] ?? false}
                            onToggle={() => setSolved((prev) => prev.map((v, j) => j === i ? !v : v))}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </>
              )}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          disabled={loading || !preview || !!preview.error || !priorityValid}
          onClick={() => void handleUpload()}
        >
          {loading ? <CircularProgress size={16} /> : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function snackbarCopy(res: Resolution, wpNumber: string): string {
  switch (res.kind) {
    case 'created': return `${wpNumber} created`;
    case 'overwritten': return `${wpNumber} updated — status and progress preserved`;
    case 'renumbered': return `${res.original_wp ?? '?'} was already in use — uploaded as ${res.assigned_wp}`;
  }
}
